/**
 * RelatorioGeraisTable - Generic sortable/paginated table for reports
 */
import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

import { getStatusLabel } from '../../constants/status';

export interface Column {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  format?: 'number' | 'decimal' | 'percent' | 'date' | 'datetime' | 'multiline' | 'status';
  width?: string;
  group?: string;
}

interface Props {
  columns: Column[];
  data: any[];
  pageSize?: number;
  sortable?: boolean;
  showTotals?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  highlightRow?: (row: any) => string;
  // External pagination (server-side)
  totalRecords?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

function formatValue(value: any, format?: string): string {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'number':
      return Number(value).toLocaleString('pt-BR');
    case 'decimal':
      // #11/#12: quantidades inteiras SEM casa decimal (3 em vez de 3,00).
      // Mostra até 3 casas só quando houver fração. Corrige 'casa a mais'.
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
    case 'percent':
      return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    case 'date':
      if (typeof value === 'string' && value.includes('-')) {
        const [y, m, d] = value.split('-');
        return `${d}/${m}/${y}`;
      }
      return String(value);
    case 'datetime':
      return String(value);
    case 'multiline':
      return String(value);
    // ⛔ N218 (Alana, 06/10): a API devolve o CODIGO do status ('EXECUTADA') e a
    // tela mostrava ele cru, em caixa alta. Rotulo vem do mapa compartilhado em
    // constants/status.ts -- nao de .toLowerCase() + capitalize, que erraria em
    // 'EM_EXECUCAO' e 'NAO_EXECUTADA' (underscore, acento e a segunda palavra).
    // getStatusLabel ja devolve o codigo quando nao conhece, entao status novo
    // aparece visivel em vez de sumir.
    case 'status':
      return getStatusLabel(String(value));
    default:
      return String(value);
  }
}

export const RelatorioGeraisTable: React.FC<Props> = ({
  columns,
  data,
  pageSize = 50,
  sortable = true,
  showTotals = false,
  loading = false,
  emptyMessage = 'Nenhum registro encontrado.',
  highlightRow,
  totalRecords,
  currentPage,
  totalPages: externalTotalPages,
  onPageChange,
}) => {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [internalPage, setInternalPage] = useState(1);

  const isServerPaginated = !!onPageChange;
  const activePage = isServerPaginated ? (currentPage || 1) : internalPage;

  const sortedData = useMemo(() => {
    if (!sortKey || !sortable) return data;
    return [...data].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [data, sortKey, sortDir, sortable]);

  // Client-side pagination
  const paginatedData = useMemo(() => {
    if (isServerPaginated) return sortedData;
    const start = (internalPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, internalPage, pageSize, isServerPaginated]);

  const totalPagesCalc = isServerPaginated
    ? (externalTotalPages || 1)
    : Math.ceil(sortedData.length / pageSize);

  const handleSort = (key: string) => {
    if (!sortable) return;
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPagesCalc) return;
    if (isServerPaginated && onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  // Compute totals for numeric columns
  const totals = useMemo(() => {
    if (!showTotals) return null;
    const result: Record<string, number> = {};
    columns.forEach(col => {
      if (['number', 'decimal', 'percent'].includes(col.format || '')) {
        result[col.key] = data.reduce((sum, row) => sum + (Number(row[col.key]) || 0), 0);
      }
    });
    return result;
  }, [data, columns, showTotals]);

  // Group headers
  const groups = useMemo(() => {
    const seen = new Map<string, number>();
    columns.forEach(col => {
      const g = col.group || '';
      seen.set(g, (seen.get(g) || 0) + 1);
    });
    if (seen.size <= 1 && !seen.has('')) return null;
    // Only render if there are actual groups
    if (Array.from(seen.keys()).every(k => k === '')) return null;
    return columns.map(col => col.group || '');
  }, [columns]);

  const groupHeaders = useMemo(() => {
    if (!groups) return null;
    const result: { label: string; span: number }[] = [];
    let prev = '';
    let count = 0;
    groups.forEach((g, i) => {
      if (g === prev) {
        count++;
      } else {
        if (i > 0) result.push({ label: prev, span: count });
        prev = g;
        count = 1;
      }
    });
    result.push({ label: prev, span: count });
    return result;
  }, [groups]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-8 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  const displayCount = totalRecords ?? data.length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            {groupHeaders && (
              <tr className="border-b border-gray-200 dark:border-gray-700">
                {groupHeaders.map((gh, i) => (
                  <th
                    key={i}
                    colSpan={gh.span}
                    className="px-3 py-2 text-center text-xs font-bold text-white bg-blue-600 dark:bg-blue-700 border-r border-blue-500 last:border-r-0"
                  >
                    {gh.label}
                  </th>
                ))}
              </tr>
            )}
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`px-3 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap ${
                    sortable ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-800' : ''
                  } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}
                  style={col.width ? { minWidth: col.width } : undefined}
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortable && (
                      sortKey === col.key
                        ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
                        : <ChevronsUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, ri) => {
              const highlight = highlightRow?.(row) || '';
              return (
                <tr
                  key={ri}
                  className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 ${highlight}`}
                >
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className={`px-3 py-2 text-gray-900 dark:text-gray-100 ${
                        col.format === 'multiline' ? 'whitespace-pre-line' : 'whitespace-nowrap'
                      } ${
                        col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                      }`}
                    >
                      {formatValue(row[col.key], col.format)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {showTotals && totals && (
            <tfoot>
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 font-bold">
                {columns.map((col, ci) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 text-gray-900 dark:text-gray-100 ${
                      col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                    }`}
                  >
                    {ci === 0
                      ? 'TOTAL'
                      : totals[col.key] !== undefined
                        ? formatValue(totals[col.key], col.format === 'percent' ? 'decimal' : col.format)
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {totalPagesCalc > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {displayCount} registros | Página {activePage} de {totalPagesCalc}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={activePage <= 1}
              className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {'<<'}
            </button>
            <button
              onClick={() => handlePageChange(activePage - 1)}
              disabled={activePage <= 1}
              className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {'<'}
            </button>
            <button
              onClick={() => handlePageChange(activePage + 1)}
              disabled={activePage >= totalPagesCalc}
              className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {'>'}
            </button>
            <button
              onClick={() => handlePageChange(totalPagesCalc)}
              disabled={activePage >= totalPagesCalc}
              className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {'>>'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
